import React, { useState, useMemo } from 'react';
import Loader from '@components/common/Loader/newLoaderModal';
import cn from 'classnames';
import Search from '@components/common/search/index.jsx';
import Fuse from 'fuse.js';
import { LABEL } from '../../veriables';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Styles from './orderItem.module.scss';
import { flowType } from '@utility/userInfo';
import { setFunctionModifierId } from '@reducers/orders';
import { onClickModifierHelper } from '../OrderItemDetailV2/OrderItemDetailHandler';

const isTableTop = flowType() === 'tableTop';
const isKiosk = flowType() === 'kiosk';

// Aligned with SearchModal: high-precision, multi-field, positional-agnostic
const SEARCH_OPTIONS = {
  shouldSort: true,
  includeMatches: true,
  threshold: 0.15,
  minMatchCharLength: 2,
  includeScore: true,
  ignoreLocation: true,
  keys: [
    { name: 'modifierName', weight: 1.0 },
    // { name: 'modifierAliases', weight: 0.3 },
    // { name: 'modifierKeywords', weight: 0.3 },
    // { name: 'modifierAbbreviations', weight: 0.3 },
  ],
};

// Returns the index of the first word in `text` that starts with `q` (Infinity if none)
const getWordMatchPos = (text, q) => {
  if (!text) return Infinity;
  const idx = text
    .toLowerCase()
    .split(/\W+/)
    .findIndex(w => w.startsWith(q));
  return idx === -1 ? Infinity : idx;
};

// Best (lowest) word-match position across an array of string values
const getBestWordPos = (values, q) =>
  Math.min(...(values || []).map(v => getWordMatchPos(v, q)));

const ModifierSearch = props => {
  const {
    isOpen,
    setSearchModalOpen,
    customClass,
    customClass2,
    loading,
    style,
    serverAppTheme,
    browserOrTablet,
    setOpenKeyboard,
    openKeyboard,
    modifiersHashTable,
    modifierLevelData,
    modifierData = [],
    selectedModifierPath,
    getPath,
    onModifierClickFromSearch,
    modifierGroupsHashTable,
    // modFunctions
    menusList,
    menuIndex,
    functionModifierId,
  } = props;

  console.log({ functionModifierId });

  // ─── modFunctions ────────────────────────────────────────────────────────────

  const modFunctionModifierGroupId =
    menusList?.[menuIndex]?.modFunctionModifierGroupId;

  const isModFunctionDisplay =
    !!modFunctionModifierGroupId &&
    !!modifierGroupsHashTable?.[modFunctionModifierGroupId];

  const handleModFunctionClick = modifierId => {
    if (modifierId === functionModifierId) {
      props.setFunctionModifierId(null);
    } else {
      props.setFunctionModifierId(modifierId);
    }
  };

  // ─── Build flat list of modifier links ───────────────────────────────────────

  const modifierLevelDataWithModifierNames = useMemo(() => {
    return (modifierLevelData || []).map(group => ({
      ...group,
      modifierLinks: (group.modifierLinks || []).map(link => ({
        ...link,
        modifierName: modifiersHashTable?.[link.modifierId]?.name || '',
        modifierGroupName: group.shortName || group.name || '',
        modifierGroupId: group.modifierGroupId,
        outOfStock: modifiersHashTable?.[link.modifierId]?.outOfStock || false,
        modifierAliases: modifiersHashTable?.[link.modifierId]?.aliases || [],
        modifierKeywords: modifiersHashTable?.[link.modifierId]?.keywords || [],
        modifierAbbreviations:
          modifiersHashTable?.[link.modifierId]?.abbreviations || [],
      })),
    }));
  }, [modifierLevelData, modifiersHashTable]);

  const items = useMemo(
    () =>
      modifierLevelDataWithModifierNames.flatMap(
        group => group.modifierLinks || []
      ),
    [modifierLevelDataWithModifierNames]
  );

  // ─── Search ──────────────────────────────────────────────────────────────────

  const [query, updateQuery] = useState('');

  const fuse = useMemo(() => new Fuse(items, SEARCH_OPTIONS), [items]);

  const results = useMemo(() => fuse.search(query), [fuse, query]);

  const characterResults = useMemo(() => {
    if (query.length < 2) return [];
    return results.map(r => ({ data: r.item, score: r.score ?? 1 }));
  }, [query, results]);

  // Deduplicate by modifierId, then sort using same word-position strategy as SearchModal
  const deduplicateResults = useMemo(() => {
    const unique = characterResults.filter(
      (item, index, self) =>
        index ===
        self.findIndex(i => i.data.modifierId === item.data.modifierId)
    );

    const q = query.toLowerCase();

    return unique.sort((a, b) => {
      const aItem = a.data;
      const bItem = b.data;

      const aInName = (aItem?.modifierName?.toLowerCase() || '').includes(q);
      const bInName = (bItem?.modifierName?.toLowerCase() || '').includes(q);

      // Name-match group always beats alias/keyword/abbreviation group
      if (aInName !== bInName) return aInName ? -1 : 1;

      if (aInName) {
        const aPos = getWordMatchPos(aItem?.modifierName, q);
        const bPos = getWordMatchPos(bItem?.modifierName, q);
        if (aPos !== bPos) return aPos - bPos;
      } else {
        const aOtherPos = Math.min(
          getBestWordPos(aItem?.modifierAliases, q),
          getBestWordPos(aItem?.modifierKeywords, q),
          getBestWordPos(aItem?.modifierAbbreviations, q)
        );
        const bOtherPos = Math.min(
          getBestWordPos(bItem?.modifierAliases, q),
          getBestWordPos(bItem?.modifierKeywords, q),
          getBestWordPos(bItem?.modifierAbbreviations, q)
        );
        if (aOtherPos !== bOtherPos) return aOtherPos - bOtherPos;
      }

      return a.score - b.score;
    });
  }, [characterResults, query]);

  // ─── Modifier click ──────────────────────────────────────────────────────────

  const onSearchChange = currentTarget => {
    updateQuery(currentTarget);
  };

  const isLimitHandler = ({ count, minimum, maximum }) => {
    let selector = false;
    if (minimum === maximum && count >= minimum) {
      selector = true;
    } else if (minimum !== maximum) {
      if (count >= minimum && count <= maximum) {
        selector = true;
      } else {
        selector = false;
      }
    }

    return selector;
  };

  const onOpenNestedModifierScreen = (modifierId, modifierGroupId) => {
    const isActive = (props.modifierData || []).some(
      mod => mod.split('|')[0] === `${modifierId}-${modifierGroupId}`
    );
    onClickModifierHelper({
      modifier: modifierId,
      modifierGroup: modifierGroupId,
      isActive,
      nestedModifierArray: [...(props.nestedModifierArray || [])],
    });
  };

  const menuItemClick = (data, e) => {
    if (e) e.stopPropagation();
    if (!onModifierClickFromSearch || !modifierGroupsHashTable) return;

    const modifierGroup = modifierGroupsHashTable[data.modifierGroupId];
    if (!modifierGroup) return;

    const modifierDataToPush = `${data.modifierId}-${data.modifierGroupId}`;
    const path = getPath
      ? getPath(selectedModifierPath, modifierDataToPush)
      : modifierDataToPush;
    const isActive = (modifierData || []).some(
      mod =>
        mod === path ||
        (mod && (mod.startsWith(`${path}|`) || mod.includes(path)))
    );

    const modData = JSON.parse(
      window.sessionStorage.getItem('globalMod') || '{}'
    );
    const check =
      (modData?.modifierData || []).filter(
        item => item && item.includes(data.modifierId)
      )?.length > 0;

    const count = (modifierData || []).filter(
      element =>
        element === path ||
        (element &&
          element.startsWith(`${data.modifierId}-`) &&
            element.includes(String(data.modifierGroupId)))
    ).length;

    const isLimitOver = isLimitHandler(
      count,
      modifierGroup.maximum,
      modifierGroup.minimum
    );

    if (isLimitOver && !check && modifierGroup.isWeight) {
      return;
    }
    if (!isKiosk && !isTableTop && modifierGroup.isWeight === true) {
      for (const element of modifierData || []) {
        const Idelement = element.split('|')[0]?.split('-')[1];
        const leftElement = element.split('|')[0]?.split('-')[0];
        if (
          String(data.modifierGroupId) === String(Idelement) &&
          Number(leftElement) !== data.modifierId
        ) {
          if (count === modifierGroup?.maximum && !check) {
            return;
          }
        }
      }
    }
    const linkedModifierGroups =
      modifiersHashTable[data.modifierId]?.modifierGroupLinks?.map(item => ({
        ...item,
        ...modifierGroupsHashTable[item.modifierGroupId],
      })) || [];
    const hasLinkedModifiers = linkedModifierGroups.length > 0;

    const shouldGoToNested =
      hasLinkedModifiers &&
      (linkedModifierGroups.some(e => e.minimum > 0) ||
        linkedModifierGroups.some(e => e.flowRequired));

    if (shouldGoToNested && typeof onOpenNestedModifierScreen === 'function') {
      onOpenNestedModifierScreen(data.modifierId, data.modifierGroupId);
      setSearchModalOpen(false);
      return;
    }
    const disable = false;
    if ((!disable && !isLimitOver) || isActive) {
      onModifierClickFromSearch(data);
    }
  };

  return (
    <div>
      {loading && <Loader />}
      <div
        className={cn(
          'modal',
          isOpen ? 'fade show d-block' : 'fade',
          customClass
        )}
      >
        <div className={cn(customClass2)} role="document">
          <div style={style}>
            {/* modFunctions row — displayed above the search input */}
            {isModFunctionDisplay && (
              <div
                className={cn('d-flex flex-row w-100 overflowX pb-2')}
                style={{
                  zIndex: 9,
                  background: serverAppTheme?.background_color,
                }}
              >
                {modifierGroupsHashTable[
                  modFunctionModifierGroupId
                ].modifierLinks.map(data => {
                  const isActive = functionModifierId === data.modifierId;
                  const name =
                    data.name || modifiersHashTable?.[data.modifierId]?.name;
                  if (!name) return null;
                  return (
                    <div
                      key={data.modifierId}
                      className="px-1"
                      role="presentation"
                      onClick={() => handleModFunctionClick(data.modifierId)}
                    >
                      <div
                        className={cn(
                          isActive
                            ? cn(Styles.activeModifier, 'color-white')
                            : Styles.selectedModifier,
                          'px-2 py-1 rounded'
                        )}
                        style={{
                          backgroundColor: isActive
                            ? serverAppTheme?.selected
                            : serverAppTheme?.modOptionBg,
                        }}
                      >
                        <div
                          className={cn('text-center fs_14 fw-500')}
                          style={{
                            color: serverAppTheme?.primary_text,
                            width: '90px',
                          }}
                        >
                          {name.length > 8 ? `${name.substring(0, 9)}` : name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <Search
              placeholderName={LABEL().SEARCH_ITEM}
              orderSearch
              onChange={onSearchChange}
              onClose={() => {
                setSearchModalOpen(false);
                props.setFunctionModifierId(null);
              }}
              isTop
              browserOrtablet={browserOrTablet}
              setOpenKeyboard={setOpenKeyboard}
              openKeyboard={openKeyboard}
              serverAppTheme={serverAppTheme}
              checkList
            />
            <div className={cn('d-flex flex-wrap py-2', Styles.scroll)}>
              {deduplicateResults.map(({ data: item }, idx) => (
                <div
                  className={Styles.foodGrid}
                  key={`${item?.modifierName}-${item.modifierId}-${idx}`}
                >
                  <div
                    className={cn(
                      Styles.foodItem,
                      item?.outOfStock ? Styles.searchButtonOutOfStock : ''
                    )}
                    role="button"
                    onClick={e => !item?.outOfStock && menuItemClick(item, e)}
                  >
                    {item?.outOfStock && (
                      <div className={Styles.outOfStockNumber}>86</div>
                    )}
                    <span className={Styles.foodName}>
                      {item?.modifierName}
                    </span>
                    {item?.modifierGroupName && (
                      <span className={Styles.foodCategory}>
                        {item.modifierGroupName?.length > 25
                          ? `${item.modifierGroupName.substring(0, 25)}...`
                          : item.modifierGroupName}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ orders }) => ({
  loading: orders.loading,
  modifiersHashTable: orders.modifiersHashTable,
  modifiers: orders.modifiers,
  modifierGroups: orders.modifierGroups,
  modifierGroupsHashTable: orders.modifierGroupsHashTable,
  menusList: orders.menusList,
  menuIndex: orders.menuIndex,
  functionModifierId: orders.functionModifierId,

  menuItemsHashTable: orders.menuItemsHashTable,
  modifierData: orders.modifierData,
  selectedModifierPath: orders.selectedModifierPath,
  nestedModifierArray: orders.nestedModifierArray,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ setFunctionModifierId }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ModifierSearch);
